import type { GridApi, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { GRID_ROOT_GROUP_ID, gridRowTreeSelector } from '@mui/x-data-grid-pro';
import type { QueryClient } from '@tanstack/react-query';
import { notifyManager, useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsApi } from '@/common/api/items';
import type { ItemTreeSet, ItemTreeSetEntry } from '@/common/api/types/items';
import type { FetchedTreeSet, TreeSetPredicate } from '@/common/queries/itemsTreeSetConsistency';
import {
  cancelPredicateReconciliation,
  invalidateItemsTreeSet,
  treeSetReordered,
} from '@/common/queries/itemsTreeSetConsistency';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { lazyMapFor } from '@/common/utils/lazyMapFor';
import { isPriorityOrderedTreeSetKey } from '@/features/items/queryKeys/itemsTreeSetKey';
import { bumpVisibleItemsCache } from '../context/VisibleItemsContext';
import { createReassertCycleGuard, type ReassertCycleGuard } from './reassertCycleGuard';

// Exclusive end index of rootId's subtree in the flat DFS array.
const subtreeEndIndex = (
  entries: readonly ItemTreeSetEntry[],
  byId: ReadonlyMap<number, ItemTreeSetEntry>,
  startIdx: number,
  rootId: number,
): number => {
  let end = startIdx + 1;
  while (end < entries.length) {
    let cur: ItemTreeSetEntry | undefined = entries[end];
    let isDescendant = false;
    while (cur?.parentId != null) {
      if (cur.parentId === rootId) {
        isDescendant = true;
        break;
      }
      cur = byId.get(cur.parentId);
    }
    if (!isDescendant) break;
    end++;
  }
  return end;
};

// Returns the original array unchanged if the move would cross parents or an id is missing.
export const reorderTreeSetData = (
  entries: readonly ItemTreeSetEntry[],
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
): readonly ItemTreeSetEntry[] => {
  const movedIdx = entries.findIndex((e) => e.id === movedId);
  if (movedIdx === -1) return entries;

  const byId = new Map(entries.map((e) => [e.id, e]));
  const movedParentId = entries[movedIdx]?.parentId ?? null;
  const subtreeEnd = subtreeEndIndex(entries, byId, movedIdx, movedId);
  const subtree = entries.slice(movedIdx, subtreeEnd);
  const withoutSubtree = [...entries.slice(0, movedIdx), ...entries.slice(subtreeEnd)];

  const targetIdx = withoutSubtree.findIndex((e) => e.id === targetSiblingId);
  if (targetIdx === -1) return entries;
  if ((withoutSubtree[targetIdx]?.parentId ?? null) !== movedParentId) return entries;

  const insertAt =
    position === 'above'
      ? targetIdx
      : subtreeEndIndex(withoutSubtree, byId, targetIdx, targetSiblingId);

  return [...withoutSubtree.slice(0, insertAt), ...subtree, ...withoutSubtree.slice(insertAt)];
};

// Bumped per row on each drag, so a re-drag can tell an earlier one it's been superseded.
const generationsByClient = new WeakMap<QueryClient, Map<number, number>>();
let nextGeneration = 0;
const generationsFor = (queryClient: QueryClient) => lazyMapFor(generationsByClient, queryClient);

// The one live cache subscription per row, so a re-drag can tear down the previous one directly.
const unsubscribesByClient = new WeakMap<QueryClient, Map<number, () => void>>();
const unsubscribesFor = (queryClient: QueryClient) => lazyMapFor(unsubscribesByClient, queryClient);

// The one live mutation per row, so a re-drag can abort the previous one's still-in-flight PATCH.
const abortControllersByClient = new WeakMap<QueryClient, Map<number, AbortController>>();
const abortControllersFor = (queryClient: QueryClient) =>
  lazyMapFor(abortControllersByClient, queryClient);

// Restored if the row's current drag fails. A re-drag keeps the first drag's baseline — MUI's
// `oldIndex` is recomputed from an already-mutated tree, so a later drag's own params aren't it.
type RollbackTarget = { oldParent: GridRowOrderChangeParams['oldParent']; oldIndex: number };
const rollbackBaselinesByClient = new WeakMap<QueryClient, Map<number, RollbackTarget>>();
const rollbackBaselinesFor = (queryClient: QueryClient) =>
  lazyMapFor(rollbackBaselinesByClient, queryClient);

const reorderInCache = (
  queryClient: QueryClient,
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
) => {
  // Priority-ordered variants only — a sorted view keeps the server's order untouched.
  queryClient.setQueriesData<ItemTreeSet>(
    {
      queryKey: commonQueryKeys.itemsTreeSet.all(),
      predicate: isPriorityOrderedTreeSetKey,
    },
    (cached) => {
      if (!cached) return cached;
      const data = reorderTreeSetData(cached.data, movedId, targetSiblingId, position);
      if (data === cached.data) return cached;
      return { ...cached, data: [...data] };
    },
  );
};

// Blocks a reassert only when it would close a real cycle — see reassertCycleGuard.ts.
const reassertCycleGuardsByClient = new WeakMap<QueryClient, ReassertCycleGuard>();
const reassertCycleGuardFor = (queryClient: QueryClient): ReassertCycleGuard => {
  let guard = reassertCycleGuardsByClient.get(queryClient);
  if (!guard) {
    guard = createReassertCycleGuard();
    reassertCycleGuardsByClient.set(queryClient, guard);
  }
  return guard;
};

const reassertFromNotification = (
  queryClient: QueryClient,
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
) => {
  reassertCycleGuardFor(queryClient).attempt(movedId, targetSiblingId, position, () =>
    reorderInCache(queryClient, movedId, targetSiblingId, position),
  );
};

const reorderSupersedeKey = (movedId: number) => `reorder:${movedId}`;

/**
 * Restricts evidence to the priority-ordered variant (a sorted sibling could satisfy `isReflected`
 * by coincidence) and to fetches started after this drag's own write. Never falls back to unscoped
 * evidence — nothing observing `movedId` must not read as converged.
 */
const evaluateReordered =
  (isReflected: TreeSetPredicate, movedId: number, notBefore: number) =>
  (fetched: readonly FetchedTreeSet[]): boolean =>
    fetched
      .filter(
        (f) => isPriorityOrderedTreeSetKey({ queryKey: f.queryKey }) && f.startedAt > notBefore,
      )
      .filter((f) => f.data.data.some((entry) => entry.id === movedId))
      .some((f) => isReflected(f.data.data));

export const useItemPriorityReorder = (apiRef: React.RefObject<GridApi>, instanceKey: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return React.useCallback(
    async (params: GridRowOrderChangeParams) => {
      const movedId = params.row.id as number;
      const tree = gridRowTreeSelector(apiRef);
      const parentId = params.newParent ?? GRID_ROOT_GROUP_ID;
      const parentNode = tree[parentId];
      if (!parentNode || !('children' in parentNode)) return;

      const { children } = parentNode;
      const idx = params.targetIndex;
      const previousItemId = idx > 0 ? Number(children[idx - 1]) : undefined;
      const nextItemId = idx < children.length - 1 ? Number(children[idx + 1]) : undefined;

      // Moving up, the previous sibling already preceded this row — anchor on the next one instead.
      const movedUp = params.newParent === params.oldParent && params.targetIndex < params.oldIndex;

      let relativePriority: { id: number; type: 'before' | 'after' } | undefined;
      if (movedUp && nextItemId !== undefined) {
        relativePriority = { id: nextItemId, type: 'before' };
      } else if (previousItemId !== undefined) {
        relativePriority = { id: previousItemId, type: 'after' };
      } else if (nextItemId !== undefined) {
        relativePriority = { id: nextItemId, type: 'before' };
      } else {
        return;
      }

      // Superseded before the mutation, so nothing is undefended while it's in flight.
      const generations = generationsFor(queryClient);
      const generation = ++nextGeneration;
      generations.set(movedId, generation);
      const isCurrentGeneration = () => generations.get(movedId) === generation;

      const unsubscribes = unsubscribesFor(queryClient);
      unsubscribes.get(movedId)?.();
      cancelPredicateReconciliation(queryClient, reorderSupersedeKey(movedId));
      // Its own scheduled cleanup could otherwise outlive this and wrongly block another row.
      reassertCycleGuardFor(queryClient).clear(movedId);

      const abortControllers = abortControllersFor(queryClient);
      abortControllers.get(movedId)?.abort();
      const abortController = new AbortController();
      abortControllers.set(movedId, abortController);

      const baselines = rollbackBaselinesFor(queryClient);
      if (!baselines.has(movedId)) {
        baselines.set(movedId, { oldParent: params.oldParent, oldIndex: params.oldIndex });
      }

      // Deferred past the drop frame so the cache rebuild doesn't race setRowPosition's repaint.
      const anchorId = relativePriority.id;
      const cachePosition = relativePriority.type === 'after' ? 'below' : 'above';
      const reconcileHandle = requestAnimationFrame(() => {
        // A later drag may have superseded this row by the time the frame actually runs.
        if (isCurrentGeneration()) reorderInCache(queryClient, movedId, anchorId, cachePosition);
      });

      const isReflected = treeSetReordered(movedId, anchorId, relativePriority.type);
      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        if (!isCurrentGeneration()) return;
        if (event.type !== 'updated' || event.action.type !== 'success') return;
        if (!isPriorityOrderedTreeSetKey({ queryKey: event.query.queryKey })) return;
        const data = event.query.state.data as ItemTreeSet | undefined;
        if (!data || isReflected(data.data)) return;
        // A no-op write still notifies — skip a variant missing anchorId, or this loops forever.
        if (reorderTreeSetData(data.data, movedId, anchorId, cachePosition) === data.data) return;
        reassertFromNotification(queryClient, movedId, anchorId, cachePosition);
      });
      unsubscribes.set(movedId, unsubscribe);

      const clearOwnSubscription = () => {
        unsubscribe();
        if (unsubscribes.get(movedId) === unsubscribe) unsubscribes.delete(movedId);
      };

      let confirmedAt: number;
      try {
        await ItemsApi.updateItem({
          itemId: movedId,
          data: { relativePriority },
          signal: abortController.signal,
        });
        confirmedAt = performance.now();
      } catch (error) {
        // Cancel the pending reconcile so the rollback below is the last write to the cache.
        cancelAnimationFrame(reconcileHandle);
        clearOwnSubscription();
        if (abortControllers.get(movedId) === abortController) abortControllers.delete(movedId);
        // A later drag aborted this one on purpose — not a real failure, nothing to tell the user.
        const wasAborted = (error as { name?: string } | null)?.name === 'AbortError';
        if (!wasAborted) {
          toast.error(
            t(($) => $.common.errors.global.title),
            {
              description: t(($) => $.common.errors.genericWithSupport),
            },
          );
        }
        // A later drag already owns this row — its own resolution is what matters now, not ours.
        if (!isCurrentGeneration()) return;

        generations.delete(movedId);
        // This drag is terminal — a stale edge from earlier must not outlive it and block another row.
        reassertCycleGuardFor(queryClient).clear(movedId);
        const baseline = baselines.get(movedId);
        baselines.delete(movedId);
        if (!baseline) return;

        const api = apiRef.current;
        if (!api) return;

        const oldParentId = baseline.oldParent ?? GRID_ROOT_GROUP_ID;
        const oldParentNode = gridRowTreeSelector(apiRef)[oldParentId];
        if (!oldParentNode || !('children' in oldParentNode)) return;

        const sourceId = params.row.id as string | number;
        const siblings = oldParentNode.children.filter((id) => String(id) !== String(sourceId));
        const anchor = baseline.oldIndex === 0 ? siblings[0] : siblings[baseline.oldIndex - 1];
        const position = baseline.oldIndex === 0 ? 'above' : 'below';

        if (anchor === undefined) return;

        api.setRowPosition(sourceId, anchor, position);
        reorderInCache(queryClient, movedId, Number(anchor), position);
        bumpVisibleItemsCache(queryClient, instanceKey);
        return;
      }

      if (abortControllers.get(movedId) === abortController) abortControllers.delete(movedId);
      void invalidateItemsTreeSet(queryClient);

      // A later drag already owns this row — nothing left to poll for.
      if (!isCurrentGeneration()) return;

      void reconcileAndSettle(queryClient, [movedId], {
        isReflected,
        evaluateReflected: evaluateReordered(isReflected, movedId, confirmedAt),
        supersedeKey: reorderSupersedeKey(movedId),
      })
        .catch(() => {})
        .finally(() => {
          // Cleared synchronously, unlike generations below — a fresh drag starting in the gap
          // before the deferred callback runs must not inherit this settled drag's baseline.
          if (isCurrentGeneration()) baselines.delete(movedId);
          // Deferred — clearing generations synchronously would make the subscription miss the
          // very notification this delay exists to let it catch.
          notifyManager.schedule(() => {
            clearOwnSubscription();
            if (isCurrentGeneration()) generations.delete(movedId);
          });
        });
    },
    [apiRef, queryClient, t, instanceKey],
  );
};
